import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  IconButton,
  Alert,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { TryOnService } from '../../services/try-on-service';
import { TryOnSessionStatus, Outfit } from '../../types';
import ContextualAdSlot from '../Ads/ContextualAdSlot';
import { tryOnAdContextService, TryOnAdContext } from '../../services/TryOnAdContextService';
import { useAuth } from "../../contexts/AuthContext";

interface TryOnModalProps {
  isOpen: boolean;
  sessionId: string;
  onClose: () => void;
  onDownload?: (imageUrl: string) => void;
  outfit?: Outfit; // Add outfit prop for ad targeting
}

const TryOnModal: React.FC<TryOnModalProps> = ({
  isOpen,
  sessionId,
  onClose,
  onDownload,
  outfit,
}) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<TryOnSessionStatus>(TryOnSessionStatus.PROCESSING);
  const [progress, setProgress] = useState<number>(0);
  const [resultImageUrl, setResultImageUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [adContext, setAdContext] = useState<TryOnAdContext | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Set up ad context when modal opens
  useEffect(() => {
    if (isOpen && sessionId && outfit) {
      const context: TryOnAdContext = {
        outfit,
        hasProfilePicture: true, // Assume true since try-on was initiated
        sessionId,
        status: 'processing',
        previousTryOns: [], // Data fetched from backend APIed from user history
      };
      setAdContext(context);
    }
  }, [isOpen, sessionId, outfit]);

  // Poll for try-on status when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      const controller = new AbortController();
      setAbortController(controller);
      pollTryOnStatus(controller.signal);

      // Cleanup: abort polling when modal closes or sessionId changes
      return () => {
        controller.abort();
        setAbortController(null);
      };
    }
  }, [isOpen, sessionId]);

  const pollTryOnStatus = async (signal?: AbortSignal) => {
    if (!sessionId) return;

    setLoading(false); // Don't set loading to true - it disables the cancel button
    setErrorMessage(null);

    try {
      const result = await TryOnService.pollTryOnStatus(
        sessionId,
        (progressValue) => {
          setProgress(progressValue);
          setLoading(false); // Ensure cancel button stays enabled during polling
        },
        60, // Increased maxAttempts for longer processing
        signal // Pass abort signal
      );

      setStatus(result.status);
      setProgress(result.progress || 100); // Default to 100% if completed
      
      // Update ad context with new status
      if (adContext) {
        const updatedContext = {
          ...adContext,
          status: result.status as 'processing' | 'completed' | 'failed',
          resultImageUrl: result.resultImageUrl,
          errorMessage: result.errorMessage,
        };
        setAdContext(updatedContext);
      }
      
      if (result.status === TryOnSessionStatus.COMPLETED && result.resultImageUrl) {
        setResultImageUrl(result.resultImageUrl);
      } else if (result.status === TryOnSessionStatus.FAILED) {
        setErrorMessage(result.errorMessage || 'Try-on processing failed');
      } else if (result.status === TryOnSessionStatus.CANCELLED) {
        setErrorMessage('Try-on was cancelled');
      }
    } catch (error: any) {
      // Don't show error if polling was aborted (user closed modal)
      if (error.message === 'Try-on polling aborted') {
        console.log('Try-on polling aborted by user');
        return;
      }
      
      console.error('Try-on polling failed:', error);
      setErrorMessage(error.message || 'Failed to process try-on');
      setStatus(TryOnSessionStatus.FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!resultImageUrl) return;

    try {
      if (onDownload) {
        onDownload(resultImageUrl);
      } else {
        // Fetch the image as a blob to handle CORS properly
        const response = await fetch(resultImageUrl);
        const blob = await response.blob();
        
        // Create a temporary URL for the blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `try-on-result-${sessionId}.jpg`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct link if fetch fails
      const link = document.createElement('a');
      link.href = resultImageUrl;
      link.download = `try-on-result-${sessionId}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleRetry = () => {
    setStatus(TryOnSessionStatus.PROCESSING);
    setProgress(0);
    setResultImageUrl(null);
    setErrorMessage(null);
    
    // Create new abort controller for retry
    const controller = new AbortController();
    setAbortController(controller);
    pollTryOnStatus(controller.signal);
  };

  const handleAdLoad = (adSlotId: string) => {
    if (adContext) {
      tryOnAdContextService.trackTryOnAdEngagement(
        sessionId,
        adSlotId,
        'impression',
        {
          outfitId: adContext.outfit.id,
          tryOnStatus: adContext.status,
          engagementLevel: !adContext.previousTryOns || adContext.previousTryOns.length === 0 ? 'new' : 
                          adContext.previousTryOns.length < 5 ? 'occasional' : 'frequent',
        }
      );
    }
  };

  const handleAdClick = (adSlotId: string) => {
    if (adContext) {
      tryOnAdContextService.trackTryOnAdEngagement(
        sessionId,
        adSlotId,
        'click',
        {
          outfitId: adContext.outfit.id,
          tryOnStatus: adContext.status,
          engagementLevel: !adContext.previousTryOns || adContext.previousTryOns.length === 0 ? 'new' : 
                          adContext.previousTryOns.length < 5 ? 'occasional' : 'frequent',
        }
      );
    }
  };

  const getOutfitDataForAds = () => {
    if (!adContext) return undefined;
    
    return {
      id: adContext.outfit.id,
      userId: adContext.outfit.userId,
      imageUrl: adContext.outfit.imageUrl,
      tags: adContext.outfit.tags,
      brands: adContext.outfit.aiAnalysis?.detectedClothingItems?.flatMap(item => item.suggestedBrands || []) || [],
      categories: ['fashion'],
      aiAnalysis: {
        clothingItems: adContext.outfit.aiAnalysis?.detectedClothingItems?.map(item => item.type) || [],
        style: 'casual',
        colors: adContext.outfit.aiAnalysis?.detectedClothingItems?.flatMap(item => item.colors) || [],
        occasion: 'casual',
      },
    };
  };

  const handleCancel = async () => {
    if (status === TryOnSessionStatus.PROCESSING && sessionId) {
      try {
        setLoading(true);
        
        // Abort ongoing polling first
        if (abortController) {
          abortController.abort();
        }
        
        // Then cancel the backend session
        await TryOnService.cancelTryOn(sessionId);
        console.log('Try-on session cancelled successfully');
        setStatus(TryOnSessionStatus.CANCELLED);
      } catch (error) {
        console.error('Failed to cancel try-on:', error);
        // Continue with close even if cancel fails
      } finally {
        setLoading(false);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    // Abort any ongoing polling
    if (abortController) {
      abortController.abort();
    }
    
    // Reset state when closing
    setStatus(TryOnSessionStatus.PROCESSING);
    setProgress(0);
    setResultImageUrl(null);
    setErrorMessage(null);
    setLoading(false);
    setAdContext(null);
    setAbortController(null);
    onClose();
  };

  const getDialogTitle = () => {
    switch (status) {
      case TryOnSessionStatus.PROCESSING:
        return 'Generating Try-On...';
      case TryOnSessionStatus.COMPLETED:
        return 'Try-On Complete!';
      case TryOnSessionStatus.FAILED:
        return 'Try-On Failed';
      case TryOnSessionStatus.CANCELLED:
        return 'Try-On Cancelled';
      default:
        return 'Virtual Try-On';
    }
  };

  const getProgressMessage = () => {
    if (status === TryOnSessionStatus.PROCESSING) {
      if (progress < 30) {
        return 'Preparing your images...';
      } else if (progress < 70) {
        return 'Generating your try-on...';
      } else {
        return 'Almost done...';
      }
    }
    return '';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="try-on-modal-title"
    >
      <DialogTitle id="try-on-modal-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{getDialogTitle()}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          minHeight="400px"
          justifyContent="center"
          gap={3}
        >
          {status === TryOnSessionStatus.PROCESSING && (
            <>
              <CircularProgress size={60} />
              <Typography variant="body1" color="textSecondary" textAlign="center">
                This will take a few seconds...
              </Typography>
              <Typography variant="body2" color="textSecondary" textAlign="center">
                {getProgressMessage()}
              </Typography>
              <Box width="100%" maxWidth="300px">
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  textAlign="center"
                  display="block"
                  mt={1}
                >
                  {progress}% complete
                </Typography>
              </Box>

              {/* Processing Ad Zone */}
              {adContext && (
                <>
                  <Divider sx={{ width: '100%', my: 2 }} />
                  <Box width="100%" maxWidth="300px">
                    <Typography variant="caption" color="textSecondary" textAlign="center" display="block" mb={1}>
                      While you wait...
                    </Typography>
                    <ContextualAdSlot
                      slotId="try-on-processing-banner"
                      sizes={[[300, 100], [320, 50]]}
                      outfitData={getOutfitDataForAds()}
                      onLoad={() => handleAdLoad('try-on-processing-banner')}
                      onError={(error) => console.error('Processing ad error:', error)}
                      className="try-on-processing-ad"
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {status === TryOnSessionStatus.COMPLETED && resultImageUrl && (
            <>
              <Typography variant="body1" color="success.main" textAlign="center" mb={2}>
                Your virtual try-on is ready!
              </Typography>
              <Box
                component="img"
                src={resultImageUrl}
                alt="Try-on result"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />

              {/* Completed Ad Zone */}
              {adContext && (
                <>
                  <Divider sx={{ width: '100%', my: 2 }} />
                  <Box width="100%" maxWidth="400px">
                    <Typography variant="caption" color="textSecondary" textAlign="center" display="block" mb={1}>
                      You might also like
                    </Typography>
                    <ContextualAdSlot
                      slotId="try-on-result-overlay"
                      sizes={[[300, 250], [320, 100]]}
                      outfitData={getOutfitDataForAds()}
                      onLoad={() => handleAdLoad('try-on-result-overlay')}
                      onError={(error) => console.error('Result ad error:', error)}
                      className="try-on-result-ad"
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {status === TryOnSessionStatus.FAILED && (
            <>
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2">
                  {errorMessage || 'Something went wrong while processing your try-on.'}
                </Typography>
              </Alert>
              <Typography variant="body2" color="textSecondary" textAlign="center">
                Please try again or contact support if the problem persists.
              </Typography>

              {/* Failed Ad Zone */}
              {adContext && (
                <>
                  <Divider sx={{ width: '100%', my: 2 }} />
                  <Box width="100%" maxWidth="300px">
                    <Typography variant="caption" color="textSecondary" textAlign="center" display="block" mb={1}>
                      Explore similar styles
                    </Typography>
                    <ContextualAdSlot
                      slotId="try-on-retry-banner"
                      sizes={[[300, 100], [320, 50]]}
                      outfitData={getOutfitDataForAds()}
                      onLoad={() => handleAdLoad('try-on-retry-banner')}
                      onError={(error) => console.error('Retry ad error:', error)}
                      className="try-on-retry-ad"
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {status === TryOnSessionStatus.CANCELLED && (
            <>
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2">
                  Try-on was cancelled. You can start a new try-on anytime.
                </Typography>
              </Alert>
              <Typography variant="body2" color="textSecondary" textAlign="center">
                Click "Try On" again to generate a new virtual try-on.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {status === TryOnSessionStatus.PROCESSING && (
          <Button 
            onClick={handleCancel} 
            color="secondary"
            disabled={false}
            variant="outlined"
          >
            Cancel
          </Button>
        )}

        {status === TryOnSessionStatus.COMPLETED && (
          <>
            <Button onClick={handleClose} color="secondary">
              Close
            </Button>
            <Button
              onClick={handleDownload}
              variant="contained"
              startIcon={<DownloadIcon />}
              color="primary"
            >
              Download
            </Button>
          </>
        )}

        {status === TryOnSessionStatus.FAILED && (
          <>
            <Button onClick={handleClose} color="secondary">
              Close
            </Button>
            <Button
              onClick={handleRetry}
              variant="contained"
              startIcon={<RefreshIcon />}
              color="primary"
              disabled={loading}
            >
              {loading ? 'Retrying...' : 'Retry'}
            </Button>
          </>
        )}

        {status === TryOnSessionStatus.CANCELLED && (
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TryOnModal;